import isChromatic from 'chromatic/isChromatic';

import Badge from '@klv/atoms/badge';
import Button from '@klv/atoms/button';
import Icon from '@klv/atoms/icon';
import Buttons from '@klv/molecules/buttons';

import VideoPlayButton from '../video-play-button';
import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/mediaCard.module.scss';

import type { IConditionalImage, IMediaCardProps } from './types';


const ConditionalImage = ({
  buttons, children,
}: IConditionalImage) => (
  buttons?.length && buttons[0].url
    ? (<Button openInNewTab={buttons[0].openInNewTab} rel='noreferrer' url={buttons[0].url} variant='none'>{children}</Button>)
    : (children)
);

const MediaCard = ({
  backgroundColor,
  buttonVariant = 'tertiary',
  className,
  mediaType,
  icon,
  image,
  videoDuration,
  videoUrl,
  videoPoster,
  title = '',
  bodyCopy,
  buttons,
  badges,
  onOpenVideo,
  ...props
}: IMediaCardProps) => {
  const chromatic = isChromatic();

  return (
    <div className={generateClassName(['mediaCard', className, styles.mediaCard, styles[backgroundColor]])} {...props}>
      {mediaType === 'image' && image?.sourceUrl && (
        <ConditionalImage buttons={buttons} data-chromatic='ignore'>
          <img
            alt={image?.altText}
            className={styles.image}
            height={image?.mediaDetails?.height}
            src={image?.sourceUrl}
            srcSet={image?.srcSet}
            loading={chromatic ? 'eager' : 'lazy'}
            width={image?.mediaDetails?.width}
          />
        </ConditionalImage>
      )}
      {mediaType === 'logo' && image?.sourceUrl && (
        <img
          alt={image?.altText}
          className={styles.logo}
          data-chromatic='ignore'
          height={image?.mediaDetails?.height}
          src={image?.sourceUrl}
          srcSet={image?.srcSet}
          loading={chromatic ? 'eager' : 'lazy'}
          width={image?.mediaDetails?.width}
        />
      )}
      {mediaType === 'video' && (
        <div
          className={styles.videoImageContainer}
          data-chromatic='ignore'
          onClick={() => onOpenVideo(videoUrl as string, title as string)}
        >
          <img
            alt={videoPoster?.altText}
            className={styles.image}
            height={videoPoster?.mediaDetails?.height}
            src={videoPoster?.sourceUrl}
            srcSet={videoPoster?.srcSet}
            loading={chromatic ? 'eager' : 'lazy'}
            width={videoPoster?.mediaDetails?.width}
          />
          <VideoPlayButton
            videoDuration={videoDuration}
          />
        </div>
      )}
      {mediaType === 'icon' && (
        <Icon
          color='currentColor'
          className={styles.icon}
          data-chromatic='ignore'
          iconName={icon?.iconName}
          size={3.0}
        />
      )}
      {
        badges?.length && (
          <div className={styles.badges}>
          {badges.map(badge => 
            <Badge className={styles.badge} key={badge?.badge} variant='medium' color='poppy06' text={badge?.badge} />,
          )}
          </div>
        )
      }
      { title && (
        <h3 className={generateClassName(['h6', 'semibold', styles.headline])}>
          {title}
        </h3>
      )}
      { bodyCopy && (
        <div
          className={generateClassName(['p2'])}
          dangerouslySetInnerHTML={{ __html: bodyCopy }}
        />
      )}
      { buttons?.length ? (
        <Buttons
          buttonColor={backgroundColor === 'light' ? 'dark' : 'light'}
          buttons={buttons}
          className={styles.buttons}
          variants={[buttonVariant]}
        />
      ) : '' 
      }
    </div>
  );
};

export default MediaCard;
